import { useAtomValue } from 'jotai';
import { useCallback, useMemo } from 'react';
import { FieldEntity, IField } from '../../../data-models/field2.data-model';
import { kpiConfigByIdMapAtom, quantitativeKpiConfigAtom } from '../../../services/state/KPIConfigState';
import { KpiConfigSelector } from '../KpiCollection/CustomKpiTemplate/KpiConfigSelector';
import { useKPIFieldActions } from '../hooks/useKPIFieldActions';

export interface IKPISelectorProps {
  createMetric?: (metricName: string) => void;
  onMetricSelect: (metric: IField<unknown>) => void;
  selectedMetrics: IField<unknown>[];
}

function isCustomKpi(kpi: IField<unknown>): boolean {
  return kpi.entity === FieldEntity.userKPI;
}

export function KPISelector({ createMetric, onMetricSelect, selectedMetrics }: IKPISelectorProps) {
  const { deleteKPI } = useKPIFieldActions();
  const nonTextualKpis = useAtomValue(quantitativeKpiConfigAtom);
  const kpisById = useAtomValue(kpiConfigByIdMapAtom);

  const selectedKpiIds = useMemo(() => selectedMetrics.map((m) => m.id), [selectedMetrics]);

  const handleSelect = useCallback(
    (kpiId: number) => {
      const kpi = kpisById.get(kpiId);
      if (kpi) {
        onMetricSelect(kpi);
      }
    },
    [kpisById, onMetricSelect]
  );

  const handleCreateNew = useMemo(
    () => (createMetric ? (kpiName?: string) => createMetric(kpiName ?? '') : undefined),
    [createMetric]
  );

  const handleDelete = useCallback((kpiId: number) => deleteKPI(kpiId), [deleteKPI]);

  return (
    <KpiConfigSelector
      availableKpis={nonTextualKpis}
      selectedKpiIds={selectedKpiIds}
      onSelect={handleSelect}
      onCreateNew={handleCreateNew}
      getOptionSecondaryLabel={(kpi) => (isCustomKpi(kpi) ? 'custom' : 'universal')}
      onDeleteOption={handleDelete}
    />
  );
}
